// LSP-21412: in contact-admin, a failure toast that gives the customer our support address renders as
// a yellow warning instead of the blue error toast.
//
// The rule is deliberately "the text contains the deployed support address", whoever wrote the
// sentence: QA/UI asked for "has the email → yellow, no email → blue". Most such sentences come from
// helpers/userMessages.ts (`couldNot*()`, `genericFailure()`, `supportHint()`), but hand-written copy
// or a `userFacing()` server message that gives the address is re-typed too, by design. Every toast
// passes through vue-toastification's one `filterBeforeCreate` hook (main.ts), so the decision lives
// here, once, instead of at the ~400 `toast.error(...)` call sites. An error toast without the address
// (a save blocked by field validation, most `userFacing()` server messages) stays the blue error
// toast, and info / success / warning toasts keep their own type.
//
// Admin only for now; async, live and mobile keep blue error toasts until a later release.
// Rules: AGENTS.md → "User-facing error messages (all apps)".

import { dropDuplicateToast } from "@/helpers/utilities";
import { supportEmail } from "@/helpers/userMessages";

interface ToastLike {
	type?: string;
	content?: unknown;
}

/** Re-types an error toast that gives the support address as a warning; returns any other toast as-is. */
export function routeSupportFailureToWarning<T extends ToastLike>(toast: T): T {
	if (toast.type !== "error" || typeof toast.content !== "string") return toast;
	// Read at call time, like the copy itself: window.env sets the address per deploy (EU differs).
	if (!toast.content.includes(supportEmail())) return toast;
	return { ...toast, type: "warning" };
}

/**
 * vue-toastification's `filterBeforeCreate` for contact-admin. Re-types first, so the duplicate
 * check compares the type that will actually render: a re-typed failure whose text matches a warning
 * already on screen would render identically, so suppressing it is the duplicate rule working.
 */
export function filterToastBeforeCreate<T extends ToastLike & { id?: unknown }>(toast: T, toasts: T[]): T | false {
	return dropDuplicateToast(routeSupportFailureToWarning(toast), toasts);
}
